// Alert classification and styling (CombinedMapView.swift 111-135,
// NWSAlert.swift 268-275). Order is most to least severe; it is also the
// icon-spacing precedence and the cluster priority.

export const ALERT_KINDS = [
  'flashFloodWarning',
  'floodWarning',
  'flashFloodWatch',
  'floodWatch',
  'floodAdvisory',
  'other',
] as const;

export type AlertKind = (typeof ALERT_KINDS)[number];

export interface AlertKindStyle {
  kind: AlertKind;
  label: string;
  pluralLabel: string;
  color: string;
  /** Lower wins when icons compete for space. */
  priority: number;
  /** Flash flood icons are triangles with a pulsing halo on iOS. */
  shape: 'triangle' | 'circle';
}

export const ALERT_STYLE: Record<AlertKind, AlertKindStyle> = {
  flashFloodWarning: { kind: 'flashFloodWarning', label: 'Flash Flood Warning', pluralLabel: 'Flash Flood Warnings', color: '#b71c1c', priority: 1, shape: 'triangle' },
  floodWarning: { kind: 'floodWarning', label: 'Flood Warning', pluralLabel: 'Flood Warnings', color: '#d32f2f', priority: 2, shape: 'circle' },
  flashFloodWatch: { kind: 'flashFloodWatch', label: 'Flash Flood Watch', pluralLabel: 'Flash Flood Watches', color: '#e65100', priority: 3, shape: 'triangle' },
  floodWatch: { kind: 'floodWatch', label: 'Flood Watch', pluralLabel: 'Flood Watches', color: '#f57c00', priority: 4, shape: 'circle' },
  floodAdvisory: { kind: 'floodAdvisory', label: 'Flood Advisory', pluralLabel: 'Flood Advisories', color: '#fbc02d', priority: 5, shape: 'circle' },
  other: { kind: 'other', label: 'Other', pluralLabel: 'Other', color: '#1a73e8', priority: 6, shape: 'circle' },
};

/** Toggleable kinds, in picker order. */
export const TOGGLEABLE_ALERT_KINDS = ALERT_KINDS.filter((k) => k !== 'other') as Exclude<AlertKind, 'other'>[];

/**
 * Substring classification in iOS order: "Flash Flood Warning", then
 * "Flood Warning" (not Flash, not Advisory), "Flash Flood Watch",
 * "Flood Watch" (not Flash), "Flood Advisory"; anything else is "other".
 */
export function classifyAlertEvent(event: string): AlertKind {
  if (event.includes('Flash Flood Warning')) return 'flashFloodWarning';
  if (event.includes('Flood Warning') && !event.includes('Flash') && !event.includes('Advisory')) return 'floodWarning';
  if (event.includes('Flash Flood Watch')) return 'flashFloodWatch';
  if (event.includes('Flood Watch') && !event.includes('Flash')) return 'floodWatch';
  if (event.includes('Flood Advisory')) return 'floodAdvisory';
  return 'other';
}

/** NWS severity pill colors (FloodWarningDetailView.swift 871-879). */
export function nwsSeverityColor(severity: string): string {
  switch (severity) {
    case 'Extreme': return '#9c27b0';
    case 'Severe': return '#d32f2f';
    case 'Moderate': return '#f57c00';
    case 'Minor': return '#fbc02d';
    default: return '#9e9e9e';
  }
}
